import { styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import SpecialEventBanner from './components/Chat/SpecialEventBanner';
import NavDrawer from './components/Drawer/NavDrawer';
import LoadingMask from './components/LoadingMask';
import NavBar from './components/NavBar';
import { useFeatures } from './components/Providers/FeatureProvider';
import { selectAppStatus } from './redux/reducers/appStatusReducer';
import { LinkDiv } from './theme/CustomComponents';
import { AppEnum } from './Types/enums';

const PageContainer = styled('div')({
  display: 'flex',

  'div:not(.ignore-position):not(.ignore-position *)': {
    position: 'relative',
  },
});

export const MainContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  alignItems: 'flex-start',
});

const Layout = () => {
  const features = useFeatures();
  const { selectedApp, isSwitchingApps, drawerWidth } = useSelector(selectAppStatus);

  const [outageBannerMessage, setOutageBannerMessage] = useState<string>('');

  const isExperts =
    import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI &&
    window.location.origin === import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI;

  useEffect(() => {
    const bannerMessage = import.meta.env.VITE_OUTAGE_BANNER;
    if (bannerMessage) {
      setOutageBannerMessage(bannerMessage);
    }
  }, []);

  // Update CSS variable when outage banner changes
  useEffect(() => {
    const outageBanner = document.querySelector('#outage-banner');
    const specialEventBanner = document.querySelector('#special-event-banner');
    let bannersHeight = 0;

    if (outageBanner && outageBannerMessage) {
      const outageBannerHeight = outageBanner.getBoundingClientRect().height;
      bannersHeight += outageBannerHeight;
    }

    if (specialEventBanner) {
      const specialEventBannerHeight = specialEventBanner.getBoundingClientRect().height;
      bannersHeight += specialEventBannerHeight;
    }

    document.documentElement.style.setProperty('--banner-height', `${bannersHeight}px`);
  });

  const specialEventBannerProps =
    selectedApp === AppEnum.DEAL_GPT
      ? {
          localStorageName: 'bannerDismissedDecember18IA',
          title: '🧠 Focused Chat is here (Beta)',
          subtitle:
            'Chat with a narrowed knowledge corpus in Intellio Advantage. Try this new feature and share your feedback.',
          linkURL: 'https://howto.westmonroe.com/a/2015425-focused-chat-work-in-intellio-advantage',
          linkText: 'Learn More',
        }
      : {
          localStorageName: 'bannerDismissedApril27Nigel',
          title: (
            <>
              <LinkDiv
                sx={{ display: 'inline', marginLeft: '6px', color: '#0047FF' }}
                onClick={() => {
                  window.open('https://howto.westmonroe.com/a/2034823', '_blank');
                }}
              >
                <i>{'New Models & Integrations'}</i>
              </LinkDiv>
              : GPT 5.5 is now available
            </>
          ),
          subtitle: 'External prompt links now publish to the new Nigel library.',
        };

  return (
    <PageContainer>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {outageBannerMessage && (
          <div
            id="outage-banner"
            style={{
              width: '100%',
              minHeight: '60px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'red',
            }}
          >
            <Typography sx={{ color: 'white', textAlign: 'center' }}>
              {outageBannerMessage}
              <br />
              ⚠️ If you are experiencing issues, please try using Nigel at a later time. ⚠️
            </Typography>
          </div>
        )}
        {!isExperts && features.special_event_banner && selectedApp !== AppEnum.DEAL_GPT && (
          <SpecialEventBanner {...specialEventBannerProps} />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: 'calc(100vh - var(--banner-height, 0px))',
            overflow: 'hidden',
          }}
        >
          <LoadingMask isLoading={isSwitchingApps} verticalAlignment="center" />
          <NavDrawer />
          <MainContainer>
            <NavBar />
            <div
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                width: `calc(100vw - ${drawerWidth})`,
                paddingLeft: '20px',
                paddingRight: '20px',
                boxSizing: 'border-box',
              }}
            >
              <Outlet />
            </div>
          </MainContainer>
        </div>
      </div>
    </PageContainer>
  );
};

export default Layout;
